import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Rectangle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to update map view when coordinates change
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1] && map && map._loaded) {
      try {
        // Only update view if map is loaded
        map.setView(center, zoom);
      } catch (error) {
        console.error('Error updating map view:', error);
      }
    }
  }, [center, zoom, map]);
  return null;
}

// Component to handle map clicks
function MapClickHandler({ onClick }) {
  useMapEvents({
    click: (e) => {
      onClick(e.latlng);
    },
  });
  return null;
}

// Calculate rectangle bounds for pool
function getPoolBounds(lat, lon, length, width) {
  // Approximate: 1 meter ≈ 0.00001 degrees at mid-latitudes
  const metersPerDegreeLat = 111320;
  const metersPerDegreeLon = 111320 * Math.cos((lat * Math.PI) / 180);
  
  const offsetLat = (length / 2) / metersPerDegreeLat;
  const offsetLon = (width / 2) / metersPerDegreeLon;
  
  return [
    [lat - offsetLat, lon - offsetLon], // Southwest
    [lat + offsetLat, lon + offsetLon], // Northeast
  ];
}

const PoolMap = ({ 
  center = [40.4168, -3.7038], // Default: Madrid
  zoom = 15,
  onLocationSelect,
  poolDimensions = null,
  editable = true
}) => {
  const [mapCenter, setMapCenter] = useState(center);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);

  useEffect(() => {
    if (center && center[0] && center[1]) {
      setMapCenter(center);
      setMapZoom(zoom);
    }
  }, [center, zoom]);

  const handleMapClick = (latlng) => {
    if (editable && onLocationSelect) {
      onLocationSelect({
        latitude: latlng.lat,
        longitude: latlng.lng
      });
      setMapCenter([latlng.lat, latlng.lng]);
      setMapZoom(18);
    }
  };

  // Calculate pool rectangle if dimensions are provided
  const poolBounds = poolDimensions && mapCenter[0] && mapCenter[1]
    ? getPoolBounds(mapCenter[0], mapCenter[1], poolDimensions.length, poolDimensions.width)
    : null;

  return (
    <div style={{ height: '400px', width: '100%', borderRadius: '12px', overflow: 'hidden', border: '2px solid #e2e8f0' }}>
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        whenReady={() => {
          setIsMapReady(true);
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {isMapReady && <ChangeView center={mapCenter} zoom={mapZoom} />}
        
        {editable && <MapClickHandler onClick={handleMapClick} />}
        
        {mapCenter[0] && mapCenter[1] && (
          <Marker position={mapCenter}>
            <Popup>
              <div className="text-sm">
                <strong>Ubicación de la piscina</strong><br />
                Lat: {mapCenter[0].toFixed(6)}<br />
                Lon: {mapCenter[1].toFixed(6)}
              </div>
            </Popup>
          </Marker>
        )}
        
        {poolBounds && (
          <Rectangle
            bounds={poolBounds}
            pathOptions={{ 
              color: '#75CCF5', 
              weight: 3,
              fillColor: '#75CCF5',
              fillOpacity: 0.3 
            }}
          >
            <Popup>
              <div className="text-sm">
                <strong>Dimensiones de la piscina</strong><br />
                {poolDimensions.length} m x {poolDimensions.width} m
              </div>
            </Popup>
          </Rectangle>
        )}
      </MapContainer>
    </div>
  );
};

export default PoolMap;
